import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, createRoute, Outlet, redirect } from '@tanstack/react-router';
import { fetchSession, type SessionData } from '../lib/auth-client';
import { AppLayout } from '../shell/AppLayout';
import { PortalLayout } from '../shell/PortalLayout';
import { RootErrorPage, NotFoundPage } from '../shell/RootPages';

/**
 * Parent routes that modules attach their routes to. Module route files import from here
 * (never from router/index.tsx) to avoid import cycles:
 *
 *   createRoute({ getParentRoute: () => appLayoutRoute, path: '/crm/contacts', component: ContactsPage })
 *   createRoute({ getParentRoute: () => portalLayoutRoute, path: '/projects/$projectId', component: ... })
 */

export type RouterContext = { queryClient: QueryClient };

export const SESSION_QUERY_KEY = ['auth', 'session'] as const;

/** Cached session lookup for route guards. */
export function loadSession(queryClient: QueryClient): Promise<SessionData | null> {
  return queryClient.fetchQuery({ queryKey: SESSION_QUERY_KEY, queryFn: fetchSession, staleTime: 60_000 });
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  errorComponent: RootErrorPage,
  notFoundComponent: NotFoundPage,
});

/** Signed-in team app (sidebar shell). Path-less: children define full paths like `/crm`. */
export const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    const session = await loadSession(context.queryClient);
    if (!session) throw redirect({ to: '/sign-in', search: { redirect: location.href } });
    return { session };
  },
  component: AppLayout,
});

/** Client portal (own minimal layout), mounted under /portal. Portal routes handle their own auth. */
export const portalLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal',
  component: PortalLayout,
});

/** Public pages (sign-in, sign-up, invite, ...): no shell, no guard. */
export const publicLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public',
  component: Outlet,
});
